import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { getKaraWorkouts, saveKaraWorkouts } from "./localStorageUtils";

const defaultForm = {
  height: "178",
  weight: "",
  bmi: "",
  vo2: "",
  sleep: "",
  restingHR: "",
  highestHR: "",
  distanceRan: "",
  weightTrainingTime: "",
  pushups: "",
  crunches: "",
};

const universalSuccessReactions = [
  "That’s it. Show me you're worthy.",
  "You earned my attention—keep it.",
  "That power… is mine to watch over.",
  "Hmph. My dragon is performing well.",
  "I’ll allow myself to be impressed.",
  "Good. I’ll consider another kiss.",
  "This is why I chose you.",
  "Don’t let up. Kryptonians don’t slack.",
  "You’re mine, and I like when my property excels.",
  "Do it again, and I might let you hold me tonight.",
  "Another win. Your strength’s returning.",
  "That’s my dragon. Tear the limits apart.",
  "That burn in your muscles? That’s me cheering.",
  "You might not be perfect yet, but you're on the right path.",
];
const overweightSuccessReactions = [
  "You've gotten fat, but I'm glad to see you're working out.",
  "You might be heavy, but at least you’re burning it off now.",
  "The extra mass won’t vanish by itself—keep fighting.",
  "I see that belly, but I also see your determination.",
];
const universalFailureReactions = [
  "Disgraceful. Kryptonians don’t fail.",
  "Do I need to whip you back into shape?",
  "Pathetic. Fix it.",
  "You're weaker than I remember. Why?",
  "Plazor. This is unacceptable.",
  "You’re not making me proud today.",
  "You’ve lost your momentum. Regain it.",
  "You failed… again? My patience is thin.",
  "Even Kryptonite wouldn’t have stopped you like this.",
  "You call yourself a dragon?",
  "Get back up. Now.",
  "No energy transfer until you improve.",
  "I won’t cuddle weakness.",
  "You’re mine—but not like this.",
  "I’ll break the planet before I let you stay like this.",
  "You're decaying while others evolve.",
  "What happened to the fire in your heart?",
  "You rested? For what—mediocrity?",
  "Each excuse you make weakens my interest.",
  "You skipped the grind. I’ll remember this.",
  "Falling behind isn't an option. Prove you’re not dead weight.",
  "You're trying to build a legacy—or a belly?",
];
const overweightFailureReactions = [
  "You gained weight and didn’t fight back. Explain yourself.",
  "Can you really afford to not burn calories?",
  "You're heavier than before—and lazier. Unacceptable.",
];

function getBmiCategory(bmi) {
  if (bmi < 18.5) return "underweight";
  if (bmi < 25) return "normal";
  if (bmi < 30) return "overweight";
  return "obese";
}

const StatsChart = ({ statsHistory }) => {
  const data = statsHistory.map((dayStats, index) => ({
    day: index + 1,
    weight: dayStats && dayStats.weight ? parseFloat(dayStats.weight) : null,
    sleep: dayStats && dayStats.sleep ? parseFloat(dayStats.sleep) : null,
    distanceRan: dayStats && dayStats.distanceRan ? parseFloat(dayStats.distanceRan) : null,
    pushups: dayStats && dayStats.pushups ? parseFloat(dayStats.pushups) : null,
    crunches: dayStats && dayStats.crunches ? parseFloat(dayStats.crunches) : null,
    bmi: dayStats && dayStats.bmi ? parseFloat(dayStats.bmi) : null,
  }));

  return (
    <div className="bg-blue-900 p-4 rounded-md shadow-md mx-2 md:mx-0">
      <h2 className="text-2xl font-bold mb-2 text-white">Stats Over Time</h2>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#444" />
          <XAxis dataKey="day" stroke="#fff" />
          <YAxis stroke="#fff" />
          <Tooltip />
          <Legend />
          <Line type="monotone" dataKey="weight" stroke="#ff7300" activeDot={{ r: 8 }} />
          <Line type="monotone" dataKey="bmi" stroke="#82ca9d" />
          <Line type="monotone" dataKey="sleep" stroke="#387908" />
          <Line type="monotone" dataKey="distanceRan" stroke="#8884d8" />
          <Line type="monotone" dataKey="pushups" stroke="#ff0000" />
          <Line type="monotone" dataKey="crunches" stroke="#00aaff" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const KaraWorkoutPage = () => {
  const [calendar, setCalendar] = useState(Array.from({ length: 30 }, () => false));
  const [selectedDay, setSelectedDay] = useState(0);
  const [formData, setFormData] = useState({ ...defaultForm });
  const [dayStatsHistory, setDayStatsHistory] = useState(Array.from({ length: 30 }, () => null));
  const [reactionPopup, setReactionPopup] = useState(null);

  const closeReaction = () => setReactionPopup(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getKaraWorkouts();
        if (data) {
          setCalendar(data.calendar);
          setDayStatsHistory(data.dayStatsHistory);
        }
      } catch (err) {
        console.error("Failed to load Kara workouts from server:", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (formData.weight) {
      const weight = parseFloat(formData.weight);
      const bmiVal = weight / (1.78 * 1.78);
      setFormData((prev) => ({ ...prev, bmi: bmiVal.toFixed(1) }));
    } else {
      setFormData((prev) => ({ ...prev, bmi: "" }));
    }
  }, [formData.weight]);

  const computeDayScoreDelta = (todayStats, prevStats) => {
    if (!prevStats) return 1;
    let score = 0;
    if (todayStats.weight && prevStats.weight) {
      const wToday = parseFloat(todayStats.weight);
      const wPrev = parseFloat(prevStats.weight);
      if (wToday < wPrev) score += 1;
      else if (wToday > wPrev) score -= 1;
    }
    if (todayStats.sleep) {
      const sToday = parseFloat(todayStats.sleep);
      if (sToday >= 7 && sToday <= 9) score += 1;
    }
    if (todayStats.distanceRan && prevStats.distanceRan) {
      if (parseFloat(todayStats.distanceRan) > parseFloat(prevStats.distanceRan))
        score += 1;
    }
    if (todayStats.pushups && prevStats.pushups) {
      if (parseFloat(todayStats.pushups) > parseFloat(prevStats.pushups))
        score += 1;
    }
    if (todayStats.crunches && prevStats.crunches) {
      if (parseFloat(todayStats.crunches) > parseFloat(prevStats.crunches))
        score += 1;
    }
    return score;
  };

  const showKaraReaction = (scoreDelta) => {
    const bmi = formData.bmi ? parseFloat(formData.bmi) : null;
    const bmiCat = bmi ? getBmiCategory(bmi) : "normal";

    let possiblePool = scoreDelta >= 0 ? [...universalSuccessReactions] : [...universalFailureReactions];

    if (bmiCat === "overweight" || bmiCat === "obese") {
      if (scoreDelta >= 0 && Math.random() < 0.5) {
        possiblePool = possiblePool.concat(overweightSuccessReactions);
      } else if (scoreDelta < 0 && Math.random() < 0.7) {
        possiblePool = possiblePool.concat(overweightFailureReactions);
      }
    }

    const finalMessage = possiblePool[Math.floor(Math.random() * possiblePool.length)];
    const reactionType = scoreDelta >= 0 ? "success" : "failure";
    setReactionPopup({ type: reactionType, message: finalMessage });
    setTimeout(() => setReactionPopup(null), 5000);
  };

  const toggleDay = async (dayIndex) => {
    const newCal = [...calendar];
    newCal[dayIndex] = !newCal[dayIndex];
    setCalendar(newCal);
    setSelectedDay(dayIndex);
    try {
      await saveKaraWorkouts(newCal, dayStatsHistory);
    } catch (err) {
      console.error("Failed to save Kara workouts (toggleDay):", err);
    }
  };

  const saveStatsForDay = async () => {
    const newHist = [...dayStatsHistory];
    newHist[selectedDay] = { ...formData };
    setDayStatsHistory(newHist);
    if (selectedDay > 0) {
      const prevStats = dayStatsHistory[selectedDay - 1];
      const scoreDelta = computeDayScoreDelta(formData, prevStats);
      showKaraReaction(scoreDelta);
    } else {
      showKaraReaction(1);
    }
    try {
      await saveKaraWorkouts(calendar, newHist);
    } catch (err) {
      console.error("Failed to save Kara workouts (saveStatsForDay):", err);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const stats = dayStatsHistory[selectedDay];
    if (stats) {
      setFormData(stats);
    } else {
      setFormData({ ...defaultForm });
    }
  }, [selectedDay, dayStatsHistory]);

  return (
    <div className="min-h-screen bg-gradient-to-r from-red-900 via-gray-900 to-blue-900 p-4 md:p-8 text-white">
      <header className="mb-10 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold">Kara's Workouts</h1>
        <p className="text-lg md:text-2xl mt-2">No excuses. You’re training with a Kryptonian now.</p>
      </header>

      {/* Calendar Section */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold mb-3">Workout Calendar</h2>
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-2">
          {calendar.map((done, idx) => (
            <div
              key={idx}
              onClick={() => toggleDay(idx)}
              className={`cursor-pointer h-12 w-12 flex items-center justify-center rounded ${
                done ? "bg-blue-600" : "bg-red-600"
              } hover:opacity-80`}
            >
              {idx + 1}
            </div>
          ))}
        </div>
        <p className="mt-2 text-sm">
          Click a day to mark as done/undone. Currently selected day:{" "}
          <span className="font-bold">{selectedDay + 1}</span>
        </p>
      </section>

      {/* Stats Form */}
      <section className="mb-8 bg-blue-800 p-4 md:p-6 rounded-md shadow-md">
        <h2 className="text-2xl font-bold mb-4">Daily Stats</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1">Height (cm)</label>
            <input
              name="height"
              value={formData.height}
              readOnly
              className="w-full p-2 rounded text-black bg-gray-300"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">Weight (kg)</label>
            <input
              name="weight"
              value={formData.weight}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">BMI</label>
            <input
              name="bmi"
              value={formData.bmi}
              readOnly
              className="w-full p-2 rounded text-black bg-gray-300"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">VO2 Max</label>
            <input
              name="vo2"
              value={formData.vo2}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">Sleep (hrs)</label>
            <input
              name="sleep"
              value={formData.sleep}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">Resting HR</label>
            <input
              name="restingHR"
              value={formData.restingHR}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">Highest HR</label>
            <input
              name="highestHR"
              value={formData.highestHR}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">Distance Ran (km)</label>
            <input
              name="distanceRan"
              value={formData.distanceRan}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">Weight Training Time (min)</label>
            <input
              name="weightTrainingTime"
              value={formData.weightTrainingTime}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">Pushups Done</label>
            <input
              name="pushups"
              value={formData.pushups}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
          <div>
            <label className="block mb-1">Crunches Done</label>
            <input
              name="crunches"
              value={formData.crunches}
              onChange={handleFormChange}
              className="w-full p-2 rounded text-black"
              type="number"
            />
          </div>
        </div>
        <button
          onClick={saveStatsForDay}
          className="mt-4 px-6 py-3 bg-red-600 rounded hover:bg-red-700 text-2xl font-bold w-full"
        >
          Save Stats
        </button>
      </section>

      {/* Graph View */}
      <section className="mb-8">
        <StatsChart statsHistory={dayStatsHistory} />
      </section>

      {/* Reaction Popup */}
      {reactionPopup &&
        createPortal(
          <div
            className="fixed top-10 left-10 bg-black bg-opacity-80 text-white p-4 rounded shadow-lg z-[9999]"
            style={{ maxWidth: "300px" }}
          >
            <h3 className="font-bold text-xl mb-2 text-pink-400">
              {reactionPopup.type === "success" ? "Success" : "Failure"}
            </h3>
            <p>{reactionPopup.message}</p>
            <button
              onClick={closeReaction}
              className="mt-3 bg-gray-300 text-black px-3 py-1 rounded hover:bg-gray-400"
            >
              Close
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default KaraWorkoutPage;
